import { appendAppLog } from './appLog';
import type { Quelle } from './einstellungen';

/**
 * Was eine Prüfregel weggeworfen hat — und warum.
 *
 * **Der Anlass** (Nutzer, 2026-09-17, auf Rückfrage „Ja, ins Dateilog"): `mische()` verwirft
 * **still**. Fällt ein fremder Wert durch `pruefe`, geht die Schleife zur nächsten Quelle, und
 * niemand erfährt es. Bei Relay hat das zwei Wochen gekostet: ein Farbwert kam als Zahl `0..3`
 * statt als `blau`…`grau`, die App zeigte brav die Vorgabe, und der Verwalter suchte den
 * Fehler in seinem Profil.
 *
 * ⚠️ **Nur `qr` und `mdm`** (Wahl des Nutzers). `ipad` und `app` bleiben stumm: was dort steht,
 * hat die App selbst geschrieben oder der Benutzer in einer Oberfläche gesetzt, die nur gültige
 * Werte anbietet. Eine Meldung darüber wäre Lärm über den eigenen Zustand. `qr` und `mdm`
 * kommen dagegen aus fremder Hand — genau dort entstehen Tippfehler, die niemand sieht.
 *
 * ⚠️ **Keine Oberfläche, kein Abschalter.** Das Log entsteht immer. Ein Schalter dafür hätte
 * genau den Zustand, in dem man ihn braucht, mit der Vorgabe „aus".
 */
export interface Verworfen {
  schluessel: string;
  /** Woher der unbrauchbare Wert kam. Nur `qr`, `mdm` und `pruefung` werden gemeldet. */
  quelle: Quelle;
  roh: unknown;
  /** Was die Prüfregel genommen hätte, in Worten — `einer von: relay, classic`. */
  erwartet: string;
  /** Trägt die Einstellung ein Passwort? Dann steht der Wert **nicht** in der Zeile. */
  geheim: boolean;
}

/**
 * Der Typ des gelieferten Werts.
 *
 * ⚠️ `typeof null` ist `'object'` — das führte den Leser in die Irre, deshalb der eigene Name.
 */
function typName(roh: unknown): string {
  return roh === null ? 'null' : typeof roh;
}

/**
 * Wie der Wert in der Zeile erscheint.
 *
 * ⚠️ **Zeichenketten in Anführungszeichen.** Ein führendes Leerzeichen ist der häufigste Grund,
 * warum ein sonst richtiger Wert durchfällt; ohne Anführungszeichen ist er in der Zeile nicht
 * zu sehen.
 */
function alsText(roh: unknown): string {
  return typeof roh === 'string' ? '"' + roh + '"' : String(roh);
}

/**
 * Eine Zeile für das Dateilog.
 *
 * ⚠️ **Der Typ gehört mit hinein.** „geliefert 2" allein schickt den Leser in die Werteliste;
 * „geliefert 2 (number)" zeigt ihm den wirklichen Fehler — das war der Relay-Fall.
 *
 * ⚠️ **Passwörter nie im Klartext.** Das Dateilog verlässt mit jeder Protokollsicherung das
 * Gerät. Die Länge bleibt, weil sie die eigentliche Frage beantwortet: ist überhaupt etwas
 * angekommen?
 */
export function formuliere(v: Verworfen): string {
  const wert = v.geheim
    ? '«unterdrueckt» (' + typName(v.roh) + (typeof v.roh === 'string' ? ', ' + v.roh.length + ' Zeichen' : '') + ')'
    : alsText(v.roh) + ' (' + typName(v.roh) + ')';
  return 'Einstellung verworfen: ' + v.schluessel + ' aus ' + v.quelle + ' — geliefert ' + wert + ', erwartet ' + v.erwartet;
}

/**
 * Schon gemeldet, in diesem Programmlauf.
 *
 * ⚠️ **Ohne diese Sperre wäre das Log unbrauchbar.** `leseEinstellungen` hat sechs
 * Aufrufstellen; jedes Öffnen des Einstellungsdialogs schriebe dasselbe falsche MDM-Profil ein
 * weiteres Mal, und nach einem Prüfungstag ginge die eine neue Meldung in hunderten gleichen
 * unter.
 *
 * Der Merker trägt **den Wert mit**: ändert der Verwalter sein Profil und liefert etwas anderes
 * Falsches, ist das eine neue Nachricht.
 */
const gemeldet = new Set<string>();

/** Nur für Prüfläufe — im Betrieb hält die Sperre den ganzen Programmlauf. */
export function vergissGemeldete(): void {
  gemeldet.clear();
}

/**
 * Die verworfenen Werte ins Dateilog schreiben.
 *
 * ⚠️ **Wirft nie.** `leseEinstellungen` ruft dies ohne `await` im Startweg der App; eine
 * abgewiesene Zusage wäre dort ein unbehandelter Fehler. `appendAppLog` schluckt zwar sein
 * eigenes Schreiben, legt den Ordner aber **vor** seinem `try` an — scheitert das, käme der
 * Fehler hier heraus.
 *
 * ⚠️ **Der Merker wird auch bei gescheitertem Schreiben gesetzt.** Sonst versuchte es jede der
 * sechs Aufrufstellen erneut, und ein volles Dateisystem erzeugte bei jedem Öffnen des
 * Einstellungsdialogs einen neuen vergeblichen Versuch.
 */
export async function meldeVerworfene(liste: readonly Verworfen[]): Promise<void> {
  for (const v of liste) {
    const merker = v.quelle + '|' + v.schluessel + '|' + String(v.roh);
    if (gemeldet.has(merker)) continue;
    gemeldet.add(merker);
    try {
      await appendAppLog(formuliere(v));
    } catch {
      // Kein Ordner, kein Platz — hier endet es. Ein Log, das wirft, hilft niemandem.
    }
  }
}
